// Local project persistence via IndexedDB. Project JSON in one store,
// audio assets as WAV blobs in another (decoded back on load).
import { Project, normalizeProject, PROJECT_FORMAT_VERSION, APP_VERSION } from "./model";
import { upgradeLegacyVideoAudio } from "../io/migrate";
import { Store } from "./store";
import { encodeWav } from "../io/export-wav";
import { VideoSource } from "../video/source";

const DB_NAME = "webcut";
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("projects")) db.createObjectStore("projects", { keyPath: "id" });
      if (!db.objectStoreNames.contains("blobs")) db.createObjectStore("blobs"); // key = `${projId}:${assetId}`
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(db: IDBDatabase, stores: string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => Promise<T> | T): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T;
    Promise.resolve(fn(t)).then((r) => { result = r; });
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const put = (s: IDBObjectStore, val: any, key?: IDBValidKey) =>
  new Promise<void>((res, rej) => { const r = s.put(val, key); r.onsuccess = () => res(); r.onerror = () => rej(r.error); });
const get = <T>(s: IDBObjectStore, key: IDBValidKey) =>
  new Promise<T | undefined>((res, rej) => { const r = s.get(key); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
const getAll = <T>(s: IDBObjectStore) =>
  new Promise<T[]>((res, rej) => { const r = s.getAll(); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });

export interface ProjectSummary {
  id: string; name: string; modifiedAt: number; createdAt: number; trackCount: number;
}

export async function saveProject(store: Store): Promise<void> {
  const db = await openDB();
  const proj = store.project;
  proj.formatVersion = PROJECT_FORMAT_VERSION;
  proj.savedWith = `WebCut ${APP_VERSION}`;
  await tx(db, ["projects", "blobs"], "readwrite", async (t) => {
    const ps = t.objectStore("projects");
    const bs = t.objectStore("blobs");
    await put(ps, JSON.parse(JSON.stringify(proj)));
    // Drop stored media this project no longer uses (removed assets used to linger forever).
    const keep = new Set(proj.assets.filter((a) => !a.generator).map((a) => `${proj.id}:${a.id}`));
    const keys = await new Promise<IDBValidKey[]>((res, rej) => { const r = bs.getAllKeys(); r.onsuccess = () => res(r.result as IDBValidKey[]); r.onerror = () => rej(r.error); });
    for (const k of keys) if (String(k).startsWith(proj.id + ":") && !keep.has(String(k))) bs.delete(k);
    for (const asset of proj.assets) {
      if (asset.generator) continue; // rebuilt from its spec on load
      const key = `${proj.id}:${asset.id}`;
      const existing = await get(bs, key);
      if (existing) continue;
      if (asset.kind === "video") {
        // Store the original encoded file; audio is re-decoded from it on load.
        const src = store.videos.get(asset.id);
        if (src) await put(bs, src.blob, key);
      } else {
        const buffer = store.buffers.get(asset.id);
        if (!buffer) continue;
        await put(bs, encodeWav(buffer, 32), key); // 32-bit float = exact, lossless
      }
    }
  });
  db.close();
}

export async function loadProject(id: string, store: Store, ctx: AudioContext): Promise<boolean> {
  const db = await openDB();
  let project: Project | undefined;
  const blobs = new Map<string, Blob>();
  await tx(db, ["projects", "blobs"], "readonly", async (t) => {
    project = await get<Project>(t.objectStore("projects"), id);
    if (project) {
      const bs = t.objectStore("blobs");
      for (const asset of project.assets) {
        const b = await get<Blob>(bs, `${id}:${asset.id}`);
        if (b) blobs.set(asset.id, b);
      }
    }
  });
  db.close();
  if (!project) return false;
  store.buffers.clear();
  store.videos.clear();
  for (const [assetId, blob] of blobs) {
    const asset = project.assets.find((a) => a.id === assetId);
    if (asset?.kind === "video") {
      try {
        store.videos.set(assetId, await VideoSource.load(blob));
        if (asset.hasAudio && !asset.audioAssetIds?.length) { // legacy: sound lived in the video file
          try { store.buffers.set(assetId, await ctx.decodeAudioData(await blob.arrayBuffer())); } catch { /* muted */ }
        }
      } catch (e) { console.warn("video reload failed", e); }
    } else {
      try { store.buffers.set(assetId, await ctx.decodeAudioData(await blob.arrayBuffer())); }
      catch { /* skip undecodable */ }
    }
  }
  const normalized = normalizeProject(project);
  upgradeLegacyVideoAudio(normalized, store.buffers);
  store.project = normalized;
  store.select(normalized.tracks[0]?.id ?? null);
  store.emitChange("project");
  return true;
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const db = await openDB();
  const all = await tx(db, ["projects"], "readonly", (t) => getAll<Project>(t.objectStore("projects")));
  db.close();
  return all
    .map((p) => ({ id: p.id, name: p.name, modifiedAt: p.modifiedAt, createdAt: p.createdAt, trackCount: p.tracks.length }))
    .sort((a, b) => b.modifiedAt - a.modifiedAt);
}

export async function deleteProject(id: string): Promise<void> {
  const db = await openDB();
  await tx(db, ["projects", "blobs"], "readwrite", async (t) => {
    t.objectStore("projects").delete(id);
    const bs = t.objectStore("blobs");
    const keys = await new Promise<IDBValidKey[]>((res) => {
      const r = bs.getAllKeys(); r.onsuccess = () => res(r.result as IDBValidKey[]);
    });
    for (const k of keys) if (String(k).startsWith(id + ":")) bs.delete(k);
  });
  db.close();
}
